import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  Alert,
  Snackbar,
  TextField,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Code as GitLabIcon,
  Edit as EditIcon,
  WarningAmber as WarningIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import {
  format, addDays, isWeekend, startOfMonth, endOfMonth,
  eachDayOfInterval, addMonths, subMonths, getDay, isToday,
} from 'date-fns';
import { scheduleApi, projectsApi, employeesApi } from '../../services/api';

const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function EmployeeMonthCalendar({ employeeId, month, ownerSection }) {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const allDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Monday-first padding: Mon=0 … Sun=6
  const startPad = (getDay(monthStart) + 6) % 7;
  const cells = [...Array(startPad).fill(null), ...allDays];
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  const projects = ownerSection?.projects || [];

  const projectsOnDay = (day) => {
    if (!day) return [];
    const ds = format(day, 'yyyy-MM-dd');
    return projects.filter(
      p => p.scheduled_start_date && p.scheduled_start_date <= ds && ds <= p.scheduled_end_date
    );
  };

  return (
    <Box>
      {/* Day-of-week headers */}
      <Grid container>
        {WEEK_DAYS.map(d => (
          <Grid item key={d} sx={{ width: '14.285%', textAlign: 'center', pb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">{d}</Typography>
          </Grid>
        ))}
      </Grid>

      {/* Weeks */}
      {weeks.map((week, wi) => (
        <Grid container key={wi}>
          {week.map((day, di) => {
            const weekend = day && isWeekend(day);
            const today = day && isToday(day);
            const dayProjects = projectsOnDay(day);
            return (
              <Grid
                item
                key={di}
                sx={{
                  width: '14.285%',
                  minHeight: 64,
                  border: '1px solid',
                  borderColor: 'divider',
                  p: 0.5,
                  bgcolor: weekend ? 'grey.50' : today ? 'primary.50' : 'background.paper',
                  boxSizing: 'border-box',
                }}
              >
                {day && (
                  <>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        fontWeight: today ? 'bold' : 'normal',
                        color: today ? 'primary.main' : weekend ? 'text.disabled' : 'text.secondary',
                        lineHeight: 1.4,
                      }}
                    >
                      {format(day, 'd')}
                    </Typography>
                    {dayProjects.map(p => (
                      <Tooltip key={p.project_id} title={p.project_name} arrow placement="top">
                        <Box
                          sx={{
                            mt: 0.25,
                            px: 0.75,
                            py: 0.125,
                            borderRadius: '3px',
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            fontSize: '0.62rem',
                            lineHeight: 1.6,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            cursor: 'default',
                            userSelect: 'none',
                          }}
                        >
                          {p.project_name}
                        </Box>
                      </Tooltip>
                    ))}
                  </>
                )}
              </Grid>
            );
          })}
        </Grid>
      ))}
    </Box>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const [schedule, setSchedule] = useState({ schedule: [], unowned: [] });
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [previewProject, setPreviewProject] = useState(null);
  const [previewEmployee, setPreviewEmployee] = useState(null);
  const [employeeCalMonth, setEmployeeCalMonth] = useState(new Date());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [filters, setFilters] = useState({
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(addDays(new Date(), 14), 'yyyy-MM-dd'),
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schedRes, projRes, empRes] = await Promise.all([
        scheduleApi.get(),
        projectsApi.getAll(),
        employeesApi.getAll(),
      ]);
      setSchedule(schedRes.data);
      setProjects(projRes.data);
      setEmployees(empRes.data);
    } catch {
      setSnackbar({ open: true, message: 'Failed to load data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRecompute = async () => {
    setRecomputing(true);
    try {
      const res = await scheduleApi.compute({});
      const schedRes = await scheduleApi.get();
      setSchedule(schedRes.data);
      const n = res.data.results.length;
      const estimated = res.data.results.filter(r => r.effort_estimated).length;
      setSnackbar({ open: true, message: `Schedule updated — ${n} project${n !== 1 ? 's' : ''} dated (${estimated} with effort estimate)`, severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to recompute schedule', severity: 'error' });
    } finally {
      setRecomputing(false);
    }
  };

  const handleOwnerChange = async (projectId, newOwnerId) => {
    try {
      const res = await projectsApi.update(projectId, { owner_id: newOwnerId || null });
      const owner = newOwnerId ? employees.find(e => e.id === newOwnerId) : null;
      setProjects(prev => prev.map(p =>
        p.id === projectId
          ? { ...p, owner_id: newOwnerId || null, owner: owner ? { id: owner.id, name: owner.name } : null }
          : p
      ));
      setPreviewProject(prev =>
        prev?.id === projectId
          ? { ...prev, owner_id: newOwnerId || null, owner: owner ? { id: owner.id, name: owner.name } : null }
          : prev
      );
      await scheduleApi.compute({});
      const schedRes = await scheduleApi.get();
      setSchedule(schedRes.data);

      const sync = res.data?.gitlab_sync;
      let message = 'Owner updated';
      let severity = 'success';
      if (sync?.attempted) {
        if (sync.success) {
          message = `Owner updated · ${sync.message}`;
        } else {
          message = `Owner updated · GitLab sync failed: ${sync.message}`;
          severity = 'warning';
        }
      }
      setSnackbar({ open: true, message, severity });
    } catch {
      setSnackbar({ open: true, message: 'Failed to update owner', severity: 'error' });
    }
  };

  const handleEmployeeClick = (employee) => {
    setPreviewEmployee(employee);
    setEmployeeCalMonth(new Date());
  };

  const generateDateRange = () => {
    const dates = [];
    const [sy, sm, sd] = filters.startDate.split('-').map(Number);
    const [ey, em, ed] = filters.endDate.split('-').map(Number);
    const end = new Date(ey, em - 1, ed);
    const d = new Date(sy, sm - 1, sd);
    while (d <= end) {
      if (!isWeekend(d)) dates.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    return dates;
  };

  const getProjectsForOwnerOnDate = (employeeId, date) => {
    const ownerSection = schedule.schedule.find(s => s.owner_id === employeeId);
    if (!ownerSection) return [];
    const dateStr = format(date, 'yyyy-MM-dd');
    return ownerSection.projects.filter(
      p => p.scheduled_start_date && p.scheduled_start_date <= dateStr && dateStr <= p.scheduled_end_date
    );
  };

  const dates = generateDateRange();
  const activeEmployees = employees.filter(e => e.is_active);
  const activeProjects = projects.filter(p => p.status === 'active');
  const unownedProjects = schedule.unowned || [];
  const scheduledCount = schedule.schedule.reduce(
    (acc, o) => acc + o.projects.filter(p => p.scheduled_start_date).length, 0
  );
  const needsRecompute = schedule.schedule.some(o =>
    o.projects.some(p => !p.scheduled_start_date)
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading dashboard…</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Project Dashboard
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleRecompute}
          disabled={recomputing}
        >
          {recomputing ? 'Computing…' : 'Recompute Schedule'}
        </Button>
      </Box>

      {needsRecompute && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Some projects have effort estimates but no scheduled dates. Click <strong>Recompute Schedule</strong> to generate them.
        </Alert>
      )}

      {/* Date range filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                type="date"
                label="Start Date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                type="date"
                label="End Date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Assignment calendar */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Assignment Calendar
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  {dates.map(date => (
                    <TableCell key={date.toISOString()} align="center">
                      <Box>
                        <Typography variant="body2">{format(date, 'MMM dd')}</Typography>
                        <Typography variant="caption" color="text.secondary">{format(date, 'EEE')}</Typography>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {activeEmployees.map(employee => (
                  <TableRow key={employee.id}>
                    <TableCell>
                      <Box
                        display="flex"
                        alignItems="center"
                        onClick={() => handleEmployeeClick(employee)}
                        sx={{
                          cursor: 'pointer',
                          borderRadius: 1,
                          px: 0.5,
                          py: 0.25,
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <PersonIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                          {employee.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    {dates.map(date => {
                      const dayProjects = getProjectsForOwnerOnDate(employee.id, date);
                      return (
                        <TableCell key={date.toISOString()} align="center">
                          <Box>
                            {dayProjects.map(p => {
                              const fullProject = projects.find(proj => proj.id === p.project_id);
                              const effortLabel = p.effort_estimated
                                ? `${p.estimated_effort_hours}h · ${p.calendar_days} day${p.calendar_days !== 1 ? 's' : ''}`
                                : 'No effort estimate — 1 day placeholder';
                              return (
                                <Tooltip
                                  key={p.project_id}
                                  title={`${p.project_name} · ${effortLabel}`}
                                  arrow
                                >
                                  <Chip
                                    label={p.effort_estimated ? p.project_name : `${p.project_name} ?`}
                                    size="small"
                                    variant={p.effort_estimated ? 'outlined' : 'filled'}
                                    color={p.effort_estimated ? 'default' : 'default'}
                                    onClick={() => fullProject && setPreviewProject(fullProject)}
                                    sx={{
                                      mb: 0.5,
                                      fontSize: '0.7rem',
                                      cursor: 'pointer',
                                      maxWidth: 120,
                                      opacity: p.effort_estimated ? 1 : 0.65,
                                      fontStyle: p.effort_estimated ? 'normal' : 'italic',
                                    }}
                                  />
                                </Tooltip>
                              );
                            })}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Unassigned projects */}
      {unownedProjects.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
              <WarningIcon color="warning" fontSize="small" />
              <Typography variant="h6">Unassigned Projects</Typography>
              <Chip label={unownedProjects.length} size="small" color="warning" variant="outlined" />
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {unownedProjects.map(p => {
                const fullProject = projects.find(proj => proj.id === p.project_id);
                return (
                  <Chip
                    key={p.project_id}
                    label={`${p.project_name}${p.estimated_effort_hours != null ? ` · ${p.estimated_effort_hours}h` : ''}`}
                    variant="outlined"
                    clickable
                    onClick={() => fullProject && setPreviewProject(fullProject)}
                  />
                );
              })}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Summary stats */}
      <Grid container spacing={3} sx={{ mt: 0 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Scheduled Projects</Typography>
              <Typography variant="h4">{scheduledCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Active Projects</Typography>
              <Typography variant="h4">{activeProjects.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Active Employees</Typography>
              <Typography variant="h4">{activeEmployees.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Employee detail overlay */}
      <Dialog
        open={!!previewEmployee}
        onClose={() => setPreviewEmployee(null)}
        maxWidth="md"
        fullWidth
      >
        {previewEmployee && (() => {
          const ownerSection = schedule.schedule.find(s => s.owner_id === previewEmployee.id);
          const empProjects = ownerSection?.projects || [];
          return (
            <>
              <DialogTitle sx={{ pb: 1 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <PersonIcon color="action" />
                  <Typography variant="h6">{previewEmployee.name}</Typography>
                  <Chip
                    label={`${empProjects.length} project${empProjects.length !== 1 ? 's' : ''}`}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </DialogTitle>

              <DialogContent>
                {/* Month navigation */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <IconButton size="small" onClick={() => setEmployeeCalMonth(m => subMonths(m, 1))}>
                    <ChevronLeftIcon />
                  </IconButton>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {format(employeeCalMonth, 'MMMM yyyy')}
                  </Typography>
                  <IconButton size="small" onClick={() => setEmployeeCalMonth(m => addMonths(m, 1))}>
                    <ChevronRightIcon />
                  </IconButton>
                </Box>

                <EmployeeMonthCalendar
                  employeeId={previewEmployee.id}
                  month={employeeCalMonth}
                  ownerSection={ownerSection}
                />

                {/* Project queue list */}
                <Divider sx={{ my: 2.5 }} />
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Project Queue
                </Typography>

                {empProjects.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No projects assigned.
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: 'text.secondary', pl: 0, width: 28 }}>#</TableCell>
                          <TableCell>Project</TableCell>
                          <TableCell align="right">Effort</TableCell>
                          <TableCell align="right">Days</TableCell>
                          <TableCell>Start</TableCell>
                          <TableCell>End</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {empProjects.map((p, idx) => (
                          <TableRow key={p.project_id} hover>
                            <TableCell sx={{ color: 'text.disabled', pl: 0 }}>{idx + 1}</TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                {p.client && (
                                  <Chip
                                    label={p.client.acronym}
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                )}
                                <Typography variant="body2">{p.project_name}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color={p.effort_estimated ? 'text.primary' : 'text.disabled'} fontStyle={p.effort_estimated ? 'normal' : 'italic'}>
                                {p.effort_estimated ? `${p.estimated_effort_hours}h` : '?'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color={p.effort_estimated ? 'text.primary' : 'text.disabled'} fontStyle={p.effort_estimated ? 'normal' : 'italic'}>
                                {p.calendar_days ?? '1*'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color={p.scheduled_start_date ? 'text.primary' : 'text.disabled'}>
                                {p.scheduled_start_date ?? '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color={p.scheduled_end_date ? 'text.primary' : 'text.disabled'}>
                                {p.scheduled_end_date ?? '—'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </DialogContent>

              <DialogActions>
                <Button onClick={() => setPreviewEmployee(null)}>Close</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* Project detail dialog */}
      <Dialog open={!!previewProject} onClose={() => setPreviewProject(null)} maxWidth="sm" fullWidth>
        {previewProject && (() => {
          const p = previewProject;
          const gitlabLabel = p.gitlab_url
            ? (p.client_acronym && p.gitlab_issue_id
                ? `${p.client_acronym} ${p.gitlab_issue_id}`
                : p.client_acronym || 'GitLab')
            : null;
          const statusColor =
            p.status === 'active' ? 'success' :
            p.status === 'completed' ? 'primary' :
            p.status === 'on_hold' ? 'warning' : 'default';
          return (
            <>
              <DialogTitle sx={{ pb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                  <Typography variant="h6" component="span" sx={{ lineHeight: 1.3 }}>
                    {p.name}
                  </Typography>
                  <Chip label={p.status} size="small" color={statusColor} sx={{ flexShrink: 0 }} />
                </Box>
              </DialogTitle>
              <DialogContent sx={{ pt: 1 }}>
                <Box display="flex" gap={0.5} mb={1.5} flexWrap="wrap" alignItems="center">
                  <Chip
                    icon={<EditIcon />}
                    label="Edit Project"
                    size="small"
                    variant="outlined"
                    color="primary"
                    clickable
                    onClick={() => {
                      setPreviewProject(null);
                      navigate('/projects', { state: { editProjectId: p.id } });
                    }}
                  />
                  {p.source_type === 'email' && (
                    <Tooltip title="Created from forwarded email">
                      <Chip icon={<EmailIcon />} label="Email" size="small" variant="outlined" />
                    </Tooltip>
                  )}
                  {gitlabLabel && (
                    <Chip
                      icon={<GitLabIcon />}
                      label={gitlabLabel}
                      size="small"
                      variant="outlined"
                      color="secondary"
                      component="a"
                      href={p.gitlab_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      clickable
                    />
                  )}
                </Box>

                <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                  <InputLabel>Owner</InputLabel>
                  <Select
                    value={p.owner_id ?? ''}
                    onChange={(e) => handleOwnerChange(p.id, e.target.value || null)}
                    label="Owner"
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {employees.map(emp => (
                      <MenuItem key={emp.id} value={emp.id}>{emp.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {(p.estimated_effort_hours != null || p.scheduled_start_date) && (
                  <Box display="flex" gap={2} mb={1.5} flexWrap="wrap">
                    {p.estimated_effort_hours != null && (
                      <Typography variant="body2" color="text.secondary">
                        Effort: <strong>{p.estimated_effort_hours}h</strong>
                      </Typography>
                    )}
                    {p.scheduled_start_date && (
                      <Typography variant="body2" color="text.secondary">
                        Window: <strong>
                          {p.scheduled_start_date === p.scheduled_end_date
                            ? p.scheduled_start_date
                            : `${p.scheduled_start_date} – ${p.scheduled_end_date}`}
                        </strong>
                      </Typography>
                    )}
                  </Box>
                )}

                <Box sx={{ maxHeight: 200, overflowY: 'auto', mb: 1.5, pr: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                    {p.description || 'No description'}
                  </Typography>
                </Box>

                {p.required_skills?.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      Required Skills
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {p.required_skills.map((skill, idx) => (
                        <Chip key={idx} label={skill} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setPreviewProject(null)}>Close</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Box>
  );
}

export default Dashboard;
